import React from "react";
import { Area, ComposedChart, Line, YAxis } from "recharts";
import styled from "@emotion/styled";
import { useTheme } from "@emotion/react";
import AutoSizer from "react-virtualized-auto-sizer";
import { Box } from "@mui/material";
import { useChartColors } from "@hooks";
import { Card, CardLabel } from "../../Card";

const Label = styled(CardLabel, {
  shouldForwardProp: (prop) => prop !== "percentPrefix",
})`
  padding: ${({ theme }) => theme.spacing(2, 4, 6)};
`;

const Container = styled(Card)<{ height?: string; isFullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 0;
  box-shadow: none;
  height: ${({ height }) => height ?? "192px"};
  overflow: hidden;
  box-sizing: border-box;
  position: relative;
  width: ${({ isFullWidth }) => (isFullWidth ? "100%" : "auto")};
`;

export interface MiniChartProps {
  data: { date: any; value: number }[];
  testid?: string;
  height?: string;
  onClick?: () => void;
  title: string;
  subtitle?: string;
  headline: string;
  percent?: number;
  percentPrefix?: string;
  isFullWidth?: boolean;
}

export const MiniChart = ({
  data,
  testid = "mini-card-chart",
  onClick,
  height,
  isFullWidth = false,
  ...rest
}: MiniChartProps) => {
  const theme = useTheme();
  const chartColors = useChartColors("miniChart");

  return (
    <Container onClick={onClick} height={height} isFullWidth={isFullWidth}>
      <Label {...rest} />
      <Box
        sx={{
          width: "100%",
          position: "absolute",
          bottom: "0",
          left: "0",
          height: "67px",
        }}
      >
        <AutoSizer>
          {({ width }) => (
            <div data-testid={testid}>
              <ComposedChart
                style={{ cursor: "pointer" }}
                width={width}
                height={67}
                data={data}
                margin={{
                  top: 0,
                  bottom: -11,
                }}
              >
                <defs>
                  <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="20%"
                      stopColor={chartColors.area}
                      stopOpacity={0.2}
                    />
                    <stop
                      offset="100%"
                      stopColor={theme.palette.common.white}
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>
                <Area
                  animationDuration={400}
                  type="monotone"
                  dataKey="areaValue"
                  stroke=""
                  fillOpacity={0.75}
                  fill="url(#colorUv)"
                />
                <YAxis
                  // allowDataOverflow={true}
                  hide
                  dataKey="areaValue"
                  domain={[
                    "dataMin",
                    (dataMax: number) => dataMax + Math.abs(dataMax) * 0.1,
                  ]}
                />

                <Line
                  animationDuration={400}
                  type="monotone"
                  strokeWidth={2}
                  dataKey="value"
                  stroke={chartColors.line}
                  dot={false}
                  legendType="none"
                />
              </ComposedChart>
            </div>
          )}
        </AutoSizer>
      </Box>
    </Container>
  );
};
